import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function SuspensePage(): Node {
  return html`
    <div>
      <h1>Suspense</h1>
      <p><code>Suspense</code> shows a fallback UI while async children are loading. <code>SuspenseAll</code> waits for multiple async components before revealing content.</p>

      <h2>Suspense</h2>
      ${CodeBlock(`import { html } from 'onefold';
import { Suspense } from 'onefold/suspense';

function App(): Node {
  return html\`
    <div>
      \${Suspense(
        () => UserProfile(),  // async component
        () => html\`<p>Loading profile...</p>\`  // fallback
      )}
    </div>
  \`;
}`)}

      <h2>SuspenseAll</h2>
      <p>Wait for multiple async resources before showing any content:</p>
      ${CodeBlock(`import { html } from 'onefold';
import { SuspenseAll } from 'onefold/suspense';

function Dashboard(): Node {
  return html\`
    <div>
      \${SuspenseAll(
        [() => UserStats(), () => RecentActivity(), () => Notifications()],
        () => html\`<div class="skeleton">Loading dashboard...</div>\`
      )}
    </div>
  \`;
}`)}

      ${Callout('Suspense works with createResource, lazy(), and any component that returns a Promise<Node>.')}

      <h2>Nested Suspense</h2>
      ${CodeBlock(`function App(): Node {
  return html\`
    <div>
      \${Suspense(
        () => html\`
          <div>
            <h1>Dashboard</h1>
            \${Suspense(
              () => ExpensiveChart(),
              () => html\`<div class="chart-skeleton"></div>\`
            )}
          </div>
        \`,
        () => html\`<p>Loading app...</p>\`
      )}
    </div>
  \`;
}`)}

      <h2>API</h2>
      <table>
        <tr><th>Function</th><th>Parameters</th><th>Description</th></tr>
        <tr><td><code>Suspense</code></td><td>(content, fallback)</td><td>Show fallback while content resolves.</td></tr>
        <tr><td><code>SuspenseAll</code></td><td>(contents[], fallback)</td><td>Show fallback until all contents resolve.</td></tr>
      </table>

      <h2>Try It</h2>
      <p>Click the button to simulate loading async content with a fallback:</p>

      ${Playground(`import { html, mount, createSignal } from 'onefold';
import { Suspense } from 'onefold/suspense';

function App() {
  const key = createSignal(0);

  function loadContent() {
    key.set(k => k + 1);
  }

  function AsyncPanel() {
    return Suspense(
      async () => {
        // Simulate network delay
        await new Promise(r => setTimeout(r, 1500));
        const items = ['Loaded item A', 'Loaded item B', 'Loaded item C', 'Loaded item D'];
        return html\`
          <div style="padding:16px;background:#f0fdf4;border:1px solid #86efac;border-radius:8px">
            <h4 style="color:#166534;margin-bottom:8px">Content Loaded!</h4>
            <ul style="padding-left:16px">
              \${items.map(item => html\`<li style="margin:4px 0">\${item}</li>\`)}
            </ul>
            <p style="font-size:12px;color:#666;margin-top:8px">Loaded at: \${new Date().toLocaleTimeString()}</p>
          </div>
        \`;
      },
      {
        fallback: () => html\`
          <div style="padding:24px;text-align:center;background:#f8fafc;border:1px dashed #cbd5e1;border-radius:8px">
            <div style="width:24px;height:24px;border:3px solid #e2e8f0;border-top-color:#4f46e5;border-radius:50%;animation:spin 0.8s linear infinite;margin:0 auto"></div>
            <p style="margin-top:8px;font-size:13px;color:#64748b">Loading content...</p>
          </div>
        \`,
      }
    );
  }

  return html\`
    <div>
      <h3>Suspense — Async Loading</h3>
      <p style="font-size:13px;color:#666;margin-bottom:12px">Shows fallback for 1.5s while async content loads.</p>
      <button onclick=\${loadContent} style="margin-bottom:12px">Reload Content</button>
      \${() => { key(); return AsyncPanel(); }}
      <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'Suspense — Async Fallback')}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/async/lazy-loading">Lazy Loading</a> — load components on demand with code splitting</li>
        <li><a href="/async/error-boundaries">Error Boundaries</a> — catch render errors gracefully</li>
      </ul>
    </div>
  `;
}
